import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation
} from '@angular/core';
import moment from 'moment';
import { combineLatest, ReplaySubject } from 'rxjs';
import { startWith, takeWhile } from 'rxjs/operators';
import Tabulator from 'tabulator-tables';
import { IPerDiemLocale, ITravelType } from '../../../../lookups';
import { UnsavedChangesService } from '../../../../shared/modal-dialog';
import { LoggingService } from '../../../../shared/services/logging/logging.service';
import { ExpenseStatus, TravelTypeEnum } from '../../../expense-enums';
import { IExpenseLine } from '../../../IExpenseLine';
import { IExpenseSummary } from '../../../IExpenseSummary';

@Component({
    selector: 'ef-expense-detail-list',
    templateUrl: './expense-detail-list.component.html',
    styleUrls: ['./expense-detail-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ExpenseDetailListComponent
    implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
    private ExpenseLinesSubject = new ReplaySubject<Array<IExpenseLine>>(1);
    private _expenseLines: Array<IExpenseLine>;
    @Input() set expenseLines(value: Array<IExpenseLine>) {
        this.log.verbose({
            type: 'ExpenseDetailListComponent:expenseLines:set',
            value
        });
        if (value && value.length > 0) {
            this._expenseLines = value.map(x => ({ ...x }));
        } else {
            this._expenseLines = [];
        }
        if (this.tabExpenseDetails) {
            this.tabExpenseDetails.replaceData(this._expenseLines);
            // for some reason the titles on the detail tabulator don't initiall display unless you do this.
            setTimeout(() => {
                this.tabExpenseDetails.redraw(true);
            }, 0);
        }
    }
    get expenseLines(): Array<IExpenseLine> {
        return this._expenseLines;
    }
    private _showDetails = false;
    @Input() set showDetails(value: boolean) {
        if (this.tabExpenseDetails) {
            this._showDetails = value;
            this.toggleDetails(value);
        }
    }
    get showDetails(): boolean {
        return this._showDetails;
    }
    @Input() perDiemTypes: Array<IPerDiemLocale>;
    @Input() travelTypes: Array<ITravelType>;
    /** From parent: detail/receipt edit allowed (workflow, status, report date in entry window). */
    @Input() canEdit = false;
    @Input() canOpen = false;
    private ValidatingSubject = new ReplaySubject<boolean>(1);
    private _validating = false;
    @Input() public set validating(value: boolean) {
        this.log.verbose({
            type: 'ExpenseDetailListComponent:validating:set',
            value
        });
        if (this.tabExpenseDetails) {
            if (value) {
                this.log.verbose({
                    type: 'ExpenseDetailListComponent:validating:set-showing My Task',
                    value
                });
                this.tabExpenseDetails.showColumn('CurrentTMTask');
                this.tabExpenseDetails.showColumn('My Tasks');
            } else {
                this.log.verbose({
                    type: 'ExpenseDetailListComponent:validating:set-hiding My Task',
                    value
                });
                this.tabExpenseDetails.hideColumn('CurrentTMTask');
                this.tabExpenseDetails.hideColumn('My Tasks');
            }
        }
        this.ValidatingSubject.next(value);
    }
    public get validating(): boolean {
        return this._validating;
    }
    private ApprovingSubject = new ReplaySubject<boolean>(1);
    private _approving: boolean;
    @Input() set approving(value: boolean) {
        this._approving = value;
        this.ApprovingSubject.next(value);
    }
    get approving(): boolean {
        return this._approving;
    }
    private SelectedExpenseSubject = new ReplaySubject<IExpenseSummary>(1);
    private selectedExpenseValue: IExpenseSummary;
    @Input() public set selectedExpense(value: IExpenseSummary) {
        this.log.verbose({
            type: 'ExpenseDetailListComponent:selectedExpense',
            value
        });
        this.selectedExpenseValue = value;
        this.SelectedExpenseSubject.next(value);
    }
    private ExpenseReportStatus?: ExpenseStatus;
    @Output() selectDetail = new EventEmitter<IExpenseLine>();
    @Output() editDetail = new EventEmitter<IExpenseLine>();
    @Output() deleteDetail = new EventEmitter<IExpenseLine>();
    @Output() showPdbo = new EventEmitter<IExpenseLine>();
    public selectedRowsCheckbox = { checked: false, indeterminate: false };
    private tabExpenseDetails: Tabulator;
    private active = true;
    constructor(
        private confirmservice: UnsavedChangesService,
        @Inject(LoggingService) private log: LoggingService
    ) {}

    ngOnInit() {}
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['canEdit'] && this.tabExpenseDetails) {
            this.tabExpenseDetails.redraw(true);
        }
    }
    ngOnDestroy() {
        this.active = false;
    }
    ngAfterViewInit(): void {
        this.createTabulator(this.expenseLines, []);
        const cols = this.tabExpenseDetails.getColumns(true);
        this.log.verbose({ type: 'ExpenseDetailListComponent', columns: cols });
        this.setupSubscriptions();
    }
    setupSubscriptions() {
        combineLatest([
            this.SelectedExpenseSubject.pipe(startWith(undefined)),
            this.ExpenseLinesSubject.pipe(startWith([])),
            this.ApprovingSubject.pipe(startWith(false)),
            this.ValidatingSubject.pipe(startWith(false))
        ])
            .pipe(takeWhile(() => this.active))
            .subscribe(([expense, lines, approving, validating]) => {
                this.ExpenseReportStatus =
                    expense && expense.ExpenseReportStatus;
                this.log.verbose({
                    type: 'ExpenseDetailListComponent:ApprovingSubject:ValidatingSubject',
                    validating,
                    approving,
                    expense
                });
                this.showHideColumns(approving || validating);
            });
    }

    private createTabulator(
        expenseLines: Array<IExpenseLine>,
        errors: Array<string>
    ) {
        const ed = this;
        this.log.verbose({
            type: 'ExpenseDetailListComponent:createTabulator:validating',
            validating: this.validating
        });
        this.tabExpenseDetails = new Tabulator('#tabExpenseDetails', {
            data: [], // expenseLines.map(e => e),
            maxHeight: '20vh',
            minHeight: '7vh',
            columnMinWidth: 120,
            columns: [
                // ExpenseRptNbr
                {
                    title: 'ExpenseRptNbr',
                    field: 'ExpenseRptNbr',
                    visible: false
                },
                // SequenceId
                {
                    title: 'SequenceId',
                    field: 'SequenceId',
                    visible: false
                },
                // Exp Line
                {
                    headerSort: false,
                    title: 'Exp Line',
                    field: '',
                    visible: true,
                    width: 50,
                    formatter: c => {
                        const data = c.getData() as IExpenseLine;
                        return `${data.ExpReportNbr}-${data.SequenceId}`;
                    }
                },
                //Per Diem Breakout
                {
                    headerSort: false,
                    title: 'PD Breakout',
                    field: 'PDBOstatus',
                    visible: true,
                    formatter: c => {
                        const data = c.getData() as IExpenseLine;

                        return ed.canEdit
                            ? `<a class='p-button p-button-link p-button-md p-0' style='text-decoration: underline;'>${
                                  data?.PDBOstatus ? data?.PDBOstatus : ''
                              }</a>`
                            : data?.PDBOstatus
                            ? data?.PDBOstatus
                            : '';
                    },
                    cellClick: (e, c) => {
                        const data = c.getData() as IExpenseLine;
                        ed.log.verbose({
                            type: 'ExpenseDetailListComponent:pdboClick',
                            data
                        });
                        if (
                            ed.canEdit &&
                            data?.PDBOstatus
                        ) {
                            ed.showPdboClick.bind(this)(c.getData());
                        }
                    },
                    cellTap: (e, c) => {
                        const data = c.getData() as IExpenseLine;
                        ed.log.verbose({
                            type: 'ExpenseDetailListComponent:pdboTap',
                            data
                        });
                        if (
                            ed.canEdit &&
                            data?.PDBOstatus
                        ) {
                            ed.showPdboClick.bind(this)(c.getData());
                        }
                    }
                },
                // Line Total
                {
                    headerSort: false,
                    title: 'Line Total',
                    field: 'Amount',
                    formatter: 'money',
                    formatterParams: {
                        symbol: '$'
                    },
                    visible: false
                },
                // My Tasks
                {
                    visible: ed.validating,
                    headerSort: false,
                    title: 'My Tasks',
                    field: 'CurrentTMTask',
                    formatter: 'tickCross',
                    formatterParams: { crossElement: false }
                },
                // Task
                {
                    headerSort: false,
                    title: 'Task',
                    field: 'RptTask',
                    visible: true
                },
                // Expense Item
                {
                    headerSort: false,
                    title: 'Expense Item',
                    field: 'ExpenseItem',
                    visible: true
                },
                // Calc Amount
                {
                    headerSort: false,
                    title: 'Calc Amount',
                    field: 'Amount',
                    visible: true,
                    width: 100,
                    formatterParams: {
                        symbol: '$'
                    },
                    formatter: 'money'
                },
                // Receipt Total
                {
                    headerSort: false,
                    title: 'Receipt Total',
                    field: 'Line_ReceiptTotal',
                    visible: true,
                    width: 75,
                    formatterParams: {
                        symbol: '$'
                    },
                    formatter: 'money'
                },
                // Allowable
                {
                    headerSort: false,
                    title: 'Allowable',
                    field: 'Line_Allowable',
                    visible: true,
                    width: 75,
                    formatterParams: {
                        symbol: '$'
                    },
                    formatter: 'money'
                },
                // Unallowable
                {
                    headerSort: false,
                    title: 'Unallowable',
                    field: 'Line_Unallowable',
                    visible: true,
                    width: 75,
                    formatterParams: {
                        symbol: '$'
                    },
                    formatter: 'money'
                },
                // Travel Start
                {
                    headerSort: false,
                    title: 'Travel Start',
                    field: 'TravelStartDate',
                    visible: false,
                    formatter: 'datetime',
                    formatterParams: c => {
                        return { outputFormat: 'MM/DD/YYYY' };
                    },
                    cssClass: 'expense-detail-perdiem'
                },
                // Travel End
                {
                    headerSort: false,
                    title: 'Travel End',
                    field: 'TravelEndDate',
                    visible: false,
                    formatter: 'datetime',
                    formatterParams: c => {
                        return { outputFormat: 'MM/DD/YYYY' };
                    },
                    cssClass: 'expense-detail-perdiem'
                },
                // Per Diem Type
                {
                    headerSort: false,
                    title: 'Per Diem Type',
                    field: 'PerDiemTypeID',
                    visible: false,
                    formatter: c => {
                        const data = c.getData() as IExpenseLine;
                        return this.perDiemTypes.findIndex(
                            x =>
                                x.LocaleID === data.LocaleID &&
                                x.PerDiemTypeID === data.PerDiemTypeID
                        ) < 0
                            ? c.getValue()
                            : this.perDiemTypes.find(
                                  x =>
                                      x.LocaleID === data.LocaleID &&
                                      x.PerDiemTypeID === data.PerDiemTypeID
                              ).PerDiemTypeDesc;
                    },
                    cssClass: 'expense-detail-perdiem'
                },
                // Zip
                {
                    headerSort: false,
                    title: 'Zip',
                    field: 'Zipcode1',
                    visible: false,
                    cssClass: 'expense-detail-perdiem'
                },
                // State
                {
                    headerSort: false,
                    title: 'State',
                    field: 'State1',
                    visible: false,
                    cssClass: 'expense-detail-perdiem',
                    formatter: c => {
                        const data = c.getData() as IExpenseLine;
                        return data.TravelTypeID === TravelTypeEnum.PerDiem
                            ? data.State1
                            : '';
                    }
                },
                // City
                {
                    headerSort: false,
                    title: 'City',
                    field: 'City1',
                    visible: false,
                    cssClass: 'expense-detail-perdiem',
                    formatter: c => {
                        const data = c.getData() as IExpenseLine;
                        return data.TravelTypeID === TravelTypeEnum.PerDiem
                            ? data.City1
                            : '';
                    }
                },
                // Country
                {
                    headerSort: false,
                    title: 'Country',
                    field: 'Country_Area',
                    visible: false,
                    cssClass: 'expense-detail-perdiem'
                },
                // Destination
                {
                    headerSort: false,
                    title: 'Destination',
                    field: 'Destination_Location',
                    visible: false,
                    cssClass: 'expense-detail-perdiem'
                },
                //   ]
                // },
                // // 9
                // {
                //   field: 'MileageExpenseType',
                //   title: 'Mileage Expense Type',
                //   columns: [
                // Beginning Zip
                {
                    headerSort: false,
                    title: 'Beginning Zip',
                    field: 'Zipcode1',
                    visible: false,
                    formatter: c => {
                        const data = c.getData() as IExpenseLine;
                        return data.Zipcode2 ? data.Zipcode1 : '';
                    },
                    cssClass: 'expense-detail-mileage'
                },
                // State
                {
                    headerSort: false,
                    title: 'State',
                    field: 'State1',
                    visible: false,
                    formatter: c => {
                        const data = c.getData() as IExpenseLine;
                        return data.TravelTypeID === TravelTypeEnum.Mileage
                            ? data.State1
                            : '';
                    },
                    cssClass: 'expense-detail-mileage'
                },
                // City
                {
                    headerSort: false,
                    title: 'City',
                    field: 'City1',
                    visible: false,
                    formatter: c => {
                        const data = c.getData() as IExpenseLine;
                        return data.TravelTypeID === TravelTypeEnum.Mileage
                            ? data.City1
                            : '';
                    },
                    cssClass: 'expense-detail-mileage'
                },
                // Ending Zip
                {
                    headerSort: false,
                    title: 'Ending Zip',
                    field: 'Zipcode2',
                    visible: false,
                    formatter: c => {
                        const data = c.getData() as IExpenseLine;
                        return data.Zipcode2 ? data.Zipcode2 : '';
                    },
                    cssClass: 'expense-detail-mileage'
                },
                // State
                {
                    headerSort: false,
                    title: 'State',
                    field: 'State2',
                    visible: false,
                    cssClass: 'expense-detail-mileage',
                    formatter: c => {
                        const data = c.getData() as IExpenseLine;
                        return data.TravelTypeID === TravelTypeEnum.Mileage
                            ? data.State2
                            : '';
                    }
                },
                // City
                {
                    headerSort: false,
                    title: 'City',
                    field: 'City2',
                    visible: false,
                    cssClass: 'expense-detail-mileage',
                    formatter: c => {
                        const data = c.getData() as IExpenseLine;
                        return data.TravelTypeID === TravelTypeEnum.Mileage
                            ? data.City2
                            : '';
                    }
                },
                // Mileage
                {
                    headerSort: false,
                    title: 'Mileage',
                    field: 'Mileage',
                    visible: false,
                    formatter: c => {
                        const data = c.getData() as IExpenseLine;
                        return data.TravelTypeID === TravelTypeEnum.Mileage
                            ? data.Mileage
                            : '';
                    },
                    cssClass: 'expense-detail-mileage'
                },
                // Mode of Travel
                {
                    headerSort: false,
                    title: 'Mode of Travel',
                    field: 'ModeofTravel',
                    visible: false,
                    cssClass: 'expense-detail-mileage'
                },
                // delete
                {
                    minWidth: 50,
                    width: 50,
                    title: 'Delete',
                    field: 'delete',
                    visible: false,
                    formatter: c => {
                        if (ed.ExpenseReportStatus <= ExpenseStatus.Signed) {
                            return `<i class="pi pi-trash ef-button-danger ef-circle-button redText"></i>`;
                        }
                    },
                    cellClick: (e, c) => {
                        if (ed.ExpenseReportStatus === ExpenseStatus.Signed) {
                            this.onExpenseDetailDelete.bind(this)(c.getData());
                        }
                    },
                    cellTap: (e, c) => {
                        if (ed.ExpenseReportStatus === ExpenseStatus.Signed) {
                            this.onExpenseDetailDelete.bind(this)(c.getData());
                        }
                    },
                    headerSort: false
                }
                //   ]
                // },
            ],
            layout: 'fitColumns',
            // rowClick: this.itemClick.bind(this),
            // rowTap: this.itemClick.bind(this),
            rowDblClick: this.itemDblClick.bind(this),
            rowDblTap: this.itemDblClick.bind(this),
            selectable: 1,
            initialSort: [
                { column: 'ExpenseRptNbr', dir: 'asc' },
                { column: 'SequenceId', dir: 'asc' }
            ]
        });
    }
    private itemClick(e, item) {
        const data = item.getData();
        this.log.debug({ type: 'ExpenseDetailListComponent:itemClick', data });
        this.selectDetail.emit(data);
    }
    private itemDblClick(e, item) {
        const data = item.getData();
        this.log.debug({
            type: 'ExpenseDetailListComponent:itemDblClick',
            data
        });
        if (!this.canOpen) {
            return;
        }
        this.editDetail.emit(data);
        // this.tabService.rowMouseHandler(e, row);
    }
    private toggleDetails(show: boolean) {
        const cols = this.tabExpenseDetails.getColumns(true); // .filter(c => c.getSubColumns().length);
        this.log.verbose({
            type: 'ExpenseDetailListComponent:toggleDetails',
            cols,
            show
        });
        // TODO: what about the conditionals in front
        cols.forEach((c, i) => {
            if (i > 11) {
                show ? c.show() : c.hide();
            }
        });
        // show the delete column
        cols[cols.length - 1].show();
        this.tabExpenseDetails.redraw(true);
    }

    private onExpenseDetailDelete(detail: IExpenseLine) {
        this.log.verbose({
            type: 'ExpenseDetailListComponent:onExpenseDetailDelete',
            summary: detail
        });
        this.confirm(this.deleteDetail, detail);
    }
    private showPdboClick(detail: IExpenseLine) {
        this.log.verbose({
            type: 'ExpenseDetailListComponent:showPdboClick',
            summary: detail
        });
        this.showPdbo.emit(detail);
    }
    confirm(emitter: EventEmitter<IExpenseLine>, summary: IExpenseLine) {
        this.confirmservice
            .confirmPromise({
                dialogText: 'Are you sure you want to delete this detail?'
            })
            .then(() => {
                emitter.emit(summary);
            })
            .catch(() => {});
    }
    showHideColumns(hide: boolean) {
        this.log.verbose({
            type: 'ExpenseDetailListComponent:showHideColumns',
            hide
        });
        if (this.tabExpenseDetails) {
            if (hide) {
                this.tabExpenseDetails.hideColumn('delete');
            } else {
                this.tabExpenseDetails.showColumn('delete');
            }
        }
    }
}
